import { useEffect, useMemo, useState } from 'react';
import { Button, Card } from '@atoms/index';

const Star = (props: { filled: boolean; onClick: () => void; label: string }) => (
  <button
    type="button"
    onClick={props.onClick}
    aria-label={props.label}
    className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={props.filled ? 'w-8 h-8 text-yellow-500' : 'w-8 h-8 text-gray-300'}
      fill="currentColor"
    >
      <path d="M12 17.27l5.18 3.17-1.64-5.81L20 10.24l-5.9-.5L12 4.5 9.9 9.74 4 10.24l4.46 4.39-1.64 5.81L12 17.27z" />
    </svg>
  </button>
);

export function ReviewModal(props: {
  isOpen: boolean;
  restaurantName: string;
  initialRating?: number;
  initialComment?: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: { rating: number; comment?: string }) => void;
}) {
  const [rating, setRating] = useState<number>(props.initialRating ?? 0);
  const [comment, setComment] = useState<string>(props.initialComment ?? '');

  useEffect(() => {
    if (!props.isOpen) return;
    setRating(props.initialRating ?? 0);
    setComment(props.initialComment ?? '');
  }, [props.isOpen, props.initialRating, props.initialComment]);

  useEffect(() => {
    if (!props.isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [props.isOpen, props.onClose]);

  const canSubmit = useMemo(() => rating >= 1 && rating <= 5 && !props.isSubmitting, [rating, props.isSubmitting]);

  if (!props.isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button className="absolute inset-0 bg-black/50" onClick={props.onClose} aria-label="Modal kapat" />
      <Card className="relative w-full max-w-md p-0 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-1">Siparişini Değerlendir</h3>
          <p className="text-sm text-gray-600 mb-5">{props.restaurantName}</p>

          <div className="flex items-center justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((v) => (
              <Star
                key={v}
                filled={rating >= v}
                onClick={() => setRating(v)}
                label={`${v} yıldız`}
              />
            ))}
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-2">Yorum (opsiyonel)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            className="w-full min-h-[120px] px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all"
            placeholder="Deneyimini paylaş..."
          />
          <div className="text-xs text-gray-400 mt-2 text-right">{comment.length}/1000</div>

          <div className="flex gap-3 mt-6">
            <Button variant="secondary" className="w-full" onClick={props.onClose} disabled={props.isSubmitting}>
              Vazgeç
            </Button>
            <Button
              className="w-full"
              onClick={() => props.onSubmit({ rating, comment: comment.trim() || undefined })}
              isLoading={props.isSubmitting}
              disabled={!canSubmit}
            >
              Gönder
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

